/*
 * Search drawer — search-specific logic only. Opening, closing, focus trapping,
 * Escape, click-outside, scroll lock and anchoring all come from the shared
 * OverlayPanel (assets/overlay-panel.js), the same one the header navigation
 * and cart drawer use; this panel anchors right, like the cart.
 *
 * Queries go to Shopify's predictive search through the Section Rendering API,
 * so results are rendered server-side by snippets/search-results.liquid and
 * swapped in — titles and prices are never rebuilt in JS.
 *
 * Typing is debounced and in-flight requests are aborted, so a fast typist
 * never sees results for an earlier query land after a later one.
 *
 * Progressive enhancement: the form is a real GET to /search, and without the
 * section added the header's search icon stays a plain link.
 */

// Bare name, resolved by the import map in layout/theme.liquid to the
// versioned asset URL (a relative './' path would bypass cache-busting).
import { OverlayPanel } from 'overlay-panel';

const DEBOUNCE_MS = 250;

const init = (drawer) => {
  if (!drawer || drawer.dataset.searchReady) return;
  // The editor's picker thumbnail (visual preview mode) is a flat, open render of
  // the drawer (.overlay--preview): a picture of it, with nothing to wire.
  if (window.Shopify && window.Shopify.visualPreviewMode) return;
  drawer.dataset.searchReady = '1';

  const dialog = drawer.querySelector('[data-search-dialog]');
  const input = drawer.querySelector('[data-search-input]');
  const results = drawer.querySelector('[data-search-results]');
  const status = drawer.querySelector('[data-search-status]');
  const limit = parseInt(drawer.dataset.resultsLimit, 10) || 6;
  const header = document.querySelector('[data-store-header]');
  // The header's icons group: on desktop the drawer opens level with it, over
  // the header, its right edge in line with the group's.
  const anchor = header ? header.querySelector('[data-header-actions]') || header.querySelector('[data-header-bar]') : null;
  const searchLinks = [...document.querySelectorAll('[data-search-link]')];

  // Keep the untouched idle markup so clearing the field can restore it
  // without a network round trip.
  const idleMarkup = results.innerHTML;

  let debounceTimer = null;
  let controller = null;

  const panel = new OverlayPanel({
    root: drawer,
    dialog,
    toggle: searchLinks[0] || null,
    anchor,
    origin: 'right',
    onOpen: () => {
      // The field is the point of the panel, so focus it rather than the first
      // focusable element (which would be the account shortcut).
      if (input) input.focus();
    },
  });

  const announce = (message) => {
    if (status) status.textContent = message;
  };

  const showIdle = () => {
    results.innerHTML = idleMarkup;
    announce('');
  };

  const search = async (term) => {
    if (controller) controller.abort();
    controller = new AbortController();
    drawer.setAttribute('data-busy', '');

    const url =
      `${window.Shopify?.routes?.root || '/'}search/suggest` +
      `?q=${encodeURIComponent(term)}` +
      `&resources[type]=product,collection,query` +
      `&resources[limit]=${limit}` +
      `&section_id=search-results`;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Search failed');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      // The section renders wrapped in its own shopify-section div; take the
      // inner markup so the drawer's own container stays in place.
      const section = parsed.querySelector('.shopify-section') || parsed.body;
      results.innerHTML = section.innerHTML;

      const count = results.querySelectorAll('.search-row').length;
      announce(count > 0 ? `${count} results for ${term}` : `No results for ${term}`);
    } catch (error) {
      if (error.name === 'AbortError') return; // superseded by a newer query
      announce('Search is unavailable right now.');
    } finally {
      drawer.removeAttribute('data-busy');
    }
  };

  if (input) {
    input.addEventListener('input', () => {
      const term = input.value.trim();
      window.clearTimeout(debounceTimer);
      if (!term) {
        if (controller) controller.abort();
        drawer.removeAttribute('data-busy');
        showIdle();
        return;
      }
      debounceTimer = window.setTimeout(() => search(term), DEBOUNCE_MS);
    });
  }

  drawer.querySelectorAll('[data-search-close]').forEach((closer) => {
    closer.addEventListener('click', () => panel.close());
  });

  // Take over the header's search icon while the drawer exists.
  searchLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      panel.open();
    });
  });

  document.addEventListener('search:open', () => panel.open());

  drawer.searchDrawer = panel;
};

const initAll = () => document.querySelectorAll('[data-search-drawer]').forEach(init);

if (document.readyState !== 'loading') initAll();
else document.addEventListener('DOMContentLoaded', initAll);

// Theme editor: re-wire a freshly reloaded drawer section.
document.addEventListener('shopify:section:load', (event) => {
  const drawer = event.target.querySelector('[data-search-drawer]');
  if (drawer) init(drawer);
});
